import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import AppModal from "../AppModal";
import toastError from "../../errors/toastError";

function TriggerFunnelsModal({ open, onClose, funnel, onSaved }) {
	const [message, setMessage] = useState("");
	const [funnelId, setFunnelId] = useState("");
	const [tagIds, setTagIds] = useState([]);
	const [funnelsList, setFunnelsList] = useState([]);
	const [tagsList, setTagsList] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const loadFunnels = async () => {
			try {
				const { data } = await api.get("/sales-funnel");
				const salesFunnels = Array.isArray(data) ? data : [];
				const funnelColumns = await Promise.all(
					salesFunnels.map(async (salesFunnel) => {
						const { data: columns } = await api.get("/sales-funnel/column", {
							params: {
								workspace: "contacts",
								salesFunnelId: salesFunnel.id,
							},
						});

						return (Array.isArray(columns) ? columns : []).map((column) => ({
							id: column.id,
							name: `${salesFunnel.name} - ${column.name}`,
						}));
					})
				);

				setFunnelsList(funnelColumns.flat());
			} catch (err) {
				toastError(err);
			}
		};

		const loadTags = async () => {
			try {
				const { data } = await api.get("/tags/list", {
					params: { workspace: "contacts" },
				});
				setTagsList(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		};

		if (open) {
			loadFunnels();
			loadTags();
		}
	}, [open, funnel]);

	useEffect(() => {
		if (funnel) {
			setMessage(funnel.mensagem || "");
			setFunnelId(funnel.funnelId || "");
			const stored = Array.isArray(funnel.tagIds)
				? funnel.tagIds
				: funnel.tagId
					? [funnel.tagId]
					: [];
			setTagIds(stored.map(Number).filter(Boolean));
		} else {
			setMessage("");
			setFunnelId("");
			setTagIds([]);
		}
	}, [funnel]);

	const handleSave = async (event) => {
		event.preventDefault();

		try {
			if (!message || !funnelId) {
				toast.error("Preencha a mensagem e a etapa do funil");
				return;
			}

			setLoading(true);

			if (funnel) {
				const { data } = await api.put(`/mensagemfunil/${funnel.id}`, {
					message,
					funnelId: Number(funnelId),
					tagIds,
				});
				onSaved?.(data);
				toast.success("Mensagem atualizada!");
			} else {
				const { data } = await api.post("/mensagemfunil", {
					message,
					funnelId: Number(funnelId),
					tagIds,
				});
				onSaved?.(data);
				toast.success("Mensagem criada!");
			}

			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={funnel ? "Editar mensagem do funil" : "Nova mensagem do funil"}
			subtitle="Quando o contato enviar esse texto, o card vai para a etapa e recebe as etiquetas."
			maxWidth={640}
		>
			<form onSubmit={handleSave} className="space-y-4">
				<div className="space-y-2">
					<label className="block text-sm font-medium text-default-700 dark:text-default-200">
						Mensagem
					</label>
					<textarea
						autoFocus
						rows={5}
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						className="form-input min-h-[140px] w-full"
						placeholder="Digite a mensagem do gatilho"
					/>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-2">
						<label className="block text-sm font-medium text-default-700 dark:text-default-200">
							Selecione a etapa do funil
						</label>
						<select
							value={funnelId}
							onChange={(e) => setFunnelId(e.target.value)}
							className="form-select w-full"
						>
							<option value="">Selecione</option>
							{funnelsList.map((funnelOption) => (
								<option key={funnelOption.id} value={funnelOption.id}>
									{funnelOption.name}
								</option>
							))}
						</select>
					</div>

					<div className="space-y-2">
						<label className="block text-sm font-medium text-default-700 dark:text-default-200">
							Etiquetas
						</label>
						<p className="m-0 text-xs text-default-500">Opcional. Pode marcar mais de uma.</p>
						{tagsList.length === 0 ? (
							<p className="m-0 text-sm text-default-500">Nenhuma etiqueta cadastrada.</p>
						) : (
							<div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-xl border border-default-200 p-3 dark:border-default-700">
								{tagsList.map((tag) => {
									const checked = tagIds.includes(Number(tag.id));
									return (
										<label
											key={tag.id}
											className={`flex cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${
												checked
													? "border-primary/40 bg-primary/10"
													: "border-default-200 bg-white dark:border-default-700 dark:bg-default-950"
											}`}
										>
											<input
												type="checkbox"
												className="size-3.5 rounded border-default-300 text-primary focus:ring-primary"
												checked={checked}
												onChange={() => {
													const id = Number(tag.id);
													setTagIds((current) =>
														current.includes(id)
															? current.filter((item) => item !== id)
															: [...current, id]
													);
												}}
											/>
											<span
												className="size-2.5 rounded-full"
												style={{ backgroundColor: tag.color || "#94a3b8" }}
											/>
											{tag.name}
										</label>
									);
								})}
							</div>
						)}
					</div>
				</div>

				<div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
					<button
						type="button"
						className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
						onClick={onClose}
					>
						Cancelar
					</button>
					<button type="submit" className="btn btn-sm bg-primary text-white disabled:opacity-60" disabled={loading}>
						{loading ? "Salvando..." : "Salvar"}
					</button>
				</div>
			</form>
		</AppModal>
	);
}

export default TriggerFunnelsModal;
